import React from 'react';
import Head from 'next/head';

import PostList from 'components/PostList';
import withLayoutHOF from 'components/withLayoutHOF';
import { getPaginatedPosts } from 'lib/wordpress';
import {
  BLOG_TITLE,
  BREADCRUMBS_BASE,
  calculateOffset,
  DEFAULT_REVALIDATION_TIME,
  LIST_POST_DESCRIPTION,
} from 'utils/shared';
import { BreadcrumbsItemType } from 'ui/src/components/Breadcrumbs';
import { useRouter } from 'next/router';
import { client } from 'lib/apollo/client';
import addPageStaticPath from 'utils/addPageStaticPath';
import { AllPostsPathsDocument } from 'gql/graphql-operations';
import { InferGetStaticPropsType } from 'next';
import getPaginationHrefConstructor from 'utils/getPaginationHrefConstructor';

export default function AllArticlesPage({
  posts,
  total,
  page,
}: InferGetStaticPropsType<typeof getStaticProps>): JSX.Element {
  const { query } = useRouter();

  const paginationHrefConstructor = getPaginationHrefConstructor({
    queryPageKey: 'page',
    href: { query: { page: query.page }, pathname: '/page/[page]' },
  });

  const breadcrumbsItems: BreadcrumbsItemType[] = [
    ...BREADCRUMBS_BASE,
    {
      label: 'All articles',
    },
  ];
  const title = `${BLOG_TITLE} | All articles`;
  return (
    <>
      <Head>
        <title>{title}</title>
      </Head>
      <PostList
        title="All articles"
        description={LIST_POST_DESCRIPTION}
        posts={posts}
        total={total}
        breadcrumbs={breadcrumbsItems}
        withCategories
        currentPage={page}
        paginationHrefConstructor={paginationHrefConstructor}
      />
    </>
  );
}

export const BLOG_INDEX_REVLIDATION_TIME = DEFAULT_REVALIDATION_TIME;

export const getStaticProps = withLayoutHOF(async ({ params }) => {
  if (!client) {
    throw new Error(`Internal Server Error. Error code: CLIENT_MISSING`);
  }

  const page = typeof params?.page === 'string' && /^\d+$/.exec(params.page) ? Number(params.page) : 1;
  const offSet = calculateOffset(page);
  const { data, errors } = await getPaginatedPosts(offSet);

  if (errors) {
    throw new Error(`PAGINATED_POST_QUERY_OBJECT: ${JSON.stringify(errors)}`);
  }

  const posts =
    data.posts?.edges?.map((post) => {
      if (!post?.node) return null;
      return { ...post.node, date: post.node?.date ? new Date(post.node.date).toUTCString() : '' };
    }) || [];

  const total = data.posts?.pageInfo?.offsetPagination?.total ?? 0;
  if (offSet >= total) {
    const notFound = {
      notFound: true,
    } as const;
    return notFound;
  }

  return {
    props: { posts, total, page, isPaginated: true },
    // revalidate: BLOG_INDEX_REVLIDATION_TIME, // ISR
  };
}, 'All articles');

export async function getStaticPaths() {
  if (!client) {
    throw new Error(`Internal Server Error. Error code: CLIENT_MISSING`);
  }

  const { data, errors } = await client.query({
    query: AllPostsPathsDocument,
  });

  if (errors) {
    throw new Error(`GET_TOTAL_POSTS_COUNT: ${JSON.stringify(errors)}`);
  }

  const paths = addPageStaticPath(
    {},
    data?.posts?.pageInfo?.offsetPagination?.total ?? 0,
    (page: string) => page,
    false,
  );

  return {
    paths,
    fallback: 'blocking',
  };
}
